/**
 * Admin Launch Login Component
 *
 * The way in an administrator's Launch Match opens (?mode=admin-launch): the
 * portal has made or found the match and opens the game in a new tab, a test
 * player named on the link joining it by its id — no press, no match created
 * here. The screen walk and the QA recipes join this way too.
 *
 * The card says which launch this is — the player, their class and the match,
 * the world in the kicker — and how the join stands (JoinStatus): its four
 * steps, or what refused it, said by its code with the server's words under a
 * translated title where it has none (W7), Retry gold and focused where a
 * second try can work, and the way back, the portal. After the menu's
 * Disconnect it says the player left, and waits on Rejoin rather than joining
 * again at once (S1); a reload joins again, as the link asks. A link cut
 * short names what it lacks.
 *
 * The link (domains/entry-links.ts, readLink): world, cohort, name and match —
 * the match the portal made — and the portal's language. The player joins as
 * the test player the class and the name make (testPlayerId), which the auth
 * service works out from them.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { readLink, shortMatch } from '../../domains/entry-links';
import { incompleteLink } from '../../domains/join-refusals';
import { useAuthStore } from '../../domains/auth';
import { authService } from '../../services/authentication/auth-service';
import { kickOffDocumentFullscreenFromGesture, shouldEnforceMatchFullscreen } from '../../utils/fullscreen';
import { EntryCaption, EntryCard, EntryFacts, EntryWho, JoinStatus, MissingParams, RefusalNotice } from './entry-card';
import { EntryFrame } from './entry-frame';
import { useJoinState } from './use-join-state';

export function AdminLaunchLogin({ search }: { search: string }) {
  const { t } = useTranslation();
  const link = useMemo(() => readLink(search, 'admin-launch'), [search]);
  const state = useJoinState();

  const join = useCallback(() => {
    if (link.missing) return;
    const { world, cohort, name, match } = link.parts;
    void authService.connectFromAdminLaunch(world, match, cohort, name);
  }, [link]);

  // The portal launched the match: join at once — unless the player has just left it.
  useEffect(() => {
    if (link.missing || useAuthStore.getState().leftMatch) return;
    if (shouldEnforceMatchFullscreen()) kickOffDocumentFullscreenFromGesture();
    join();
  }, [link, join]);

  const frame = { search, title: t('login.adminLaunch.title'), meaning: t('login.adminLaunch.subtitle') };

  // A link cut short: nothing here can mend it; the portal makes a new one.
  if (link.missing) {
    return (
      <EntryFrame {...frame}>
        <EntryCard>
          <RefusalNotice words={incompleteLink()} detail={<MissingParams names={link.missing} />} />
          <EntryCaption>{t('entry.refusal.reopenFromPortal')}</EntryCaption>
        </EntryCard>
      </EntryFrame>
    );
  }

  return (
    <EntryFrame {...frame} kicker={[link.parts.world]}>
      <EntryCard>
        <EntryWho label={t('entry.player')} name={link.parts.name} />
        <EntryFacts
          facts={[
            { label: t('entry.fact.class'), value: link.parts.cohort },
            { label: t('entry.fact.match'), value: shortMatch(link.parts.match) },
          ]}
        />
        <JoinStatus state={state} onJoin={join} ask="portal" />
      </EntryCard>
    </EntryFrame>
  );
}
